import React, { useState, useEffect, useCallback } from "react";
import { Form, Formik, Field } from "formik";
import * as moment from "moment";
import {
  getPostInitialValues,
  VALIDATION_SCHEMA,
} from "utils/requestPickupOrderUtils";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AlertModal from "components/AlertModal";
import { stateConvert } from "utils/stateAbbreviation";
import NumberInput from "components/FormElements/NumberInput";
import CheckboxInput from "components/FormElements/CheckboxInput";
import TextInput from "components/FormElements/TextInput";
import Textarea from "components/FormElements/TextArea";
import RadioButton from "components/FormElements/RadioButton";
import Select from "components/FormElements/Select";
import Layout from "components/Layout";
import DatePicker from "components/FormElements/DatePicker";
import TimePickerInput from "components/FormElements/TimePickerInput";
import { TopHeading, OuterWrapper } from "assets/css/global";
import HeadingIcon from "assets/svg/RequestOrderIcon";
import { BodyContainer, Footer } from "styles/pages/global";
import { pickupLocationList } from "redux/actions/requestOrders";
import {
  createOrder,
  fetchEditOrderApi,
  updateOrder,
} from "redux/actions/requestPickupOrder";
import {
  FormWrapper,
  NoVenderAvailableBannerWrapper,
} from "styles/pages/request-pickup-orders";
import { sortListOfObjects } from "helpers";
import { useText } from "locales/useGetText";
const RequestPickupOrders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locale = useText("requestPickup");
  const loggedInUserInfo = JSON.parse(localStorage.getItem("loggedInUserData"));
  const [lastChangedTime, setLastChangedTime] = useState();
  const loadingReqList = [
    { id: "0", value: "LIVE_LOAD", label: "Live Load" },
    { id: "1", value: "DROP_TRAILER", label: "Drop Trailer" },
  ];

  const [locationListResponse, setLocationListResponse] = useState();
  const [editedValues, setEditedValues] = useState();

  const [modal, setModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("Alert");
  const [modalLink, setModalLink] = useState("/");
  const toggleModal = () => setModal(!modal);

  const [successCreateOrder, setSuccessCreateOrder] = useState(false);

  useEffect(() => {
    if (!loggedInUserInfo?.token) {
      navigate("/");
    } else {
      locationListAPI();
    }
    const ordRefNoForFetchReqPickOrd = localStorage.getItem(
      "ordRefNoForFetchReqOrd",
    );
    if (ordRefNoForFetchReqPickOrd)
      fetchEditOrderDetails(ordRefNoForFetchReqPickOrd);
  }, []);

  const locationListAPI = useCallback(async () => {
    if (loggedInUserInfo?.token === null) {
      setModalContent(locale("signInAgain"));
      setModalLink("/");
      toggleModal();
    } else {
      let response = await dispatch(
        pickupLocationList(
          loggedInUserInfo?.locationType,
          loggedInUserInfo?.customerId,
          loggedInUserInfo?.token,
        ),
      );

      if (response?.data?.success) {
        const arr = response.data.data.locations.flatMap((location) => {
          let stateVal =
            stateConvert(location.mainLocation?.address?.state) ||
            location.mainLocation?.address?.state;

          const mainLocationObj = location.mainLocation
            ? [
              {
                label: `${location.mainLocation.address?.locationName}, ${location.mainLocation.address.street}, ${location.mainLocation.address.city}, ${stateVal}, ${location.mainLocation.address.zipCode}`,
                value: location.mainLocation.locationId,
                otherDetails: {
                  city: location.mainLocation.address.city,
                  contactNumber: location.mainLocation.address.contactNumber,
                  locationName: location.mainLocation.address.locationName,
                  name: location.mainLocation.address.name,
                  state: location.mainLocation.address.state,
                  country: location.mainLocation.address.country,
                  street: location.mainLocation.address.street,
                  zipCode: location.mainLocation.address.zipCode,
                },
              },
            ]
            : [];

          const subLocationsObj = location.subLocations
            ? location.subLocations.map((subLocation) => {
              stateVal =
                stateConvert(subLocation.address?.state) ||
                subLocation.address?.state;

              return {
                label: `${subLocation.address.name}, ${subLocation.address.street}, ${subLocation.address.city}, ${stateVal}, ${subLocation.address.zipCode}`,
                value: subLocation.locationId,
                otherDetails: {
                  city: subLocation.address.city,
                  contactNumber: subLocation.address.contactNumber,
                  locationName: subLocation.address.locationName,
                  name: subLocation.address.name,
                  state: subLocation.address.state,
                  country: subLocation.address?.country || location.mainLocation?.address?.country,
                  street: subLocation.address.street,
                  zipCode: subLocation.address.zipCode,
                },
              };
            })
            : [];

          return [...mainLocationObj, ...subLocationsObj];
        });
        const sortedArr = sortListOfObjects(
          "string",
          arr,
          "asc",
          "otherDetails.locationName",
        ); // sort alphabetically
        setLocationListResponse(sortedArr);
      } else {
        setModalContent(
          response?.data?.error?.message ||
          response.message ||
          locale("genericErrorLater"),
        );
        setModalLink("");
        toggleModal();
      }
    }
  }, []);

  const fetchEditOrderDetails = async (ordRefNoForFetchReqPickOrd) => {
    let response = await dispatch(
      fetchEditOrderApi(ordRefNoForFetchReqPickOrd, loggedInUserInfo?.token),
    );
    if (response.data?.success) {
      setEditedValues(response.data?.data);
    }
  };

  const formSubmit = async (props) => {
    const { values } = props;

    if (
      values.quantity === 0 ||
      values.quantity === "0" ||
      values.quantity === ""
    ) {
      setModalTitle("");
      setModalContent(locale("quantityGreaterThanZero"));
      setModalLink("");
      toggleModal();
      return;
    }

    let locationDetails = locationListResponse.filter(
      (data) => data.value === values.businessName,
    );

    let data = {
      emailId: [loggedInUserInfo?.email],
      locationName: loggedInUserInfo?.locationName,
      loadingRequirement: values.loadingReq !== "" ? values.loadingReq : null,
      loadDetails: {
        requestPickupDate: moment(values.pickUpDate).format("YYYY-MM-DD HH:mm"),
        deliveryFromtime: values.appFromTime,
        deliveryTotime: values.appToTime,
        poNumber: values.poNumber,
        quantity: values.quantity,
      },
      location: {
        locationId: values.businessName,
        shippingAddress: locationDetails[0].otherDetails,
      },
      comment: values.comments,
    };

    if (!editedValues)
      data.customer = {
        customerId: loggedInUserInfo?.customerId,
        locationType: loggedInUserInfo?.locationType,
      };
    if (editedValues) data.orderRefNumber = editedValues.orderRefNumber;

    if (!loggedInUserInfo?.token) {
      navigate("/");
    } else {
      let response;
      if (editedValues)
        response = await dispatch(updateOrder(data, loggedInUserInfo?.token));
      else
        response = await dispatch(createOrder(data, loggedInUserInfo?.token));
      if (response?.data?.success) {
        setModalTitle("");
        // setModalContent(editedValues ? locale("pickupUpdatedSuccess") : locale("pickupRequestSent"));
        setModalContent(editedValues ? 'Thank you! Pickup request updated successfully!' : 'Thank you! Your pickup request has been sent to a PalletTrader+ team member.');
        setModalLink("");
        toggleModal();
        setSuccessCreateOrder(true);
      } else {
        setModalContent(
          response?.data?.error?.message ||
          response.message ||
          locale("genericErrorLater"),
        );
        setModalLink("");
        toggleModal();
      }
    }
  };

  const resetFields = (props) => {
    const { setFieldValue } = props;
    let reqDefaultPickupDate = moment(
      new Date().setDate(new Date().getDate() + 4),
    ).format("MM/DD/YYYY");
    setFieldValue("businessName", "");
    setFieldValue("loadingReq", "");
    setFieldValue("quantity", "");
    setFieldValue("pickUpDate", reqDefaultPickupDate);
    setFieldValue("appFromTime", "00:00");
    setFieldValue("appToTime", "23:00");
    setFieldValue("poNumber", "");
    setFieldValue("comments", "");
  };

  const checkDisable = (props) => {
    const { values } = props;
    if (editedValues) {
      let initialEditValues = {
        businessName: editedValues?.locationId,
        loadingReq: editedValues?.loadingRequirement,
        quantity: editedValues?.loadDetails?.quantity,
        pickUpDate: moment(editedValues?.loadDetails?.requestPickupDate).format(
          "MM/DD/YYYY",
        ),
        appFromTime: editedValues?.loadDetails?.deliveryFromtime,
        appToTime: editedValues?.loadDetails?.deliveryTotime,
        poNumber: editedValues?.loadDetails?.poNumber,
        comments: editedValues?.comment,
      };
      let isModified = Object.keys(props.values).some(
        (field) => props.values[field] !== initialEditValues[field],
      );
      if (!isModified) return true;
    }

    let fromTimeValue = parseInt(props.values.appFromTime?.replace(":", ""));
    let toTimeValue = parseInt(props.values.appToTime?.replace(":", ""));
    if (
      values?.loadingReq === "" ||
      values.businessName === "" ||
      values.quantity === "" ||
      values.pickUpDate === "Invalid date" ||
      toTimeValue <= fromTimeValue
    )
      return true;
    else return false;
  };

  const setEditedValuesFunction = (props) => {
    const { setFieldValue } = props;
    const ordRefNoForFetchReqPickOrd = localStorage.getItem(
      "ordRefNoForFetchReqOrd",
    );
    if (editedValues && ordRefNoForFetchReqPickOrd) {
      setFieldValue("businessName", editedValues?.locationId);
      setFieldValue("loadingReq", editedValues?.loadingRequirement || "");
      setFieldValue("quantity", editedValues?.loadDetails?.quantity);
      setFieldValue(
        "pickUpDate",
        moment(editedValues?.loadDetails?.requestPickupDate).format(
          "MM/DD/YYYY",
        ),
      );
      setFieldValue("appFromTime", editedValues?.loadDetails?.deliveryFromtime);
      setFieldValue("appToTime", editedValues?.loadDetails?.deliveryTotime);
      setFieldValue("poNumber", editedValues?.loadDetails?.poNumber);
      setFieldValue("comments", editedValues?.comment);
      localStorage.removeItem("ordRefNoForFetchReqOrd");
    }
  };

  const isTimeErrorCheck = (props, type) => {
    let fromTimeValue = parseInt(props.values?.appFromTime?.replace(":", ""));
    let toTimeValue = parseInt(props.values?.appToTime?.replace(":", ""));
    if (toTimeValue <= fromTimeValue)
      return (
        <p
          style={{
            position: "absolute",
            fontSize: "13px",
            color: "##ff1e1e",
            bottom: "-22px",
          }}
        >
          Invalid Time
        </p>
      );
  };

  const formFields = () => {
    return (
      <Layout activeTab="RequestPickUpOrder">
        <OuterWrapper>
          <Formik
            initialValues={getPostInitialValues()}
            validationSchema={VALIDATION_SCHEMA()}
            onSubmit={() => { }}
          >
            {(props) => {
              setEditedValuesFunction(props);
              return (
                <>
                  <Form className="form-wrapper">
                    {locationListResponse?.length < 1 && (
                      <NoVenderAvailableBannerWrapper>
                        <p>
                          <span>{locale("noVendorPricingMessage")}</span>
                          <button onClick={() => navigate("/dashboard")}>
                            {locale("ok")}
                          </button>
                        </p>
                      </NoVenderAvailableBannerWrapper>
                    )}
                    <TopHeading>
                      <HeadingIcon />
                      <div>
                        <h4>{locale("requestNewPickupOrder")}</h4>
                      </div>
                    </TopHeading>
                    <FormWrapper>
                      <div>
                        <div>
                          <div className="label-heading">
                            <p>
                              {locale("pickupLocation")}
                              <span className="star">*</span>
                            </p>
                          </div>
                          <div className="business-name-wrapper">
                            <div className="app-input-text mb-20">
                              <Select
                                name="businessName"
                                data={
                                  locationListResponse && locationListResponse
                                }
                                placeholder={locale("companyName")}
                                searchable={true}
                                onChangeHandler={(value, response) => { }}
                              />
                            </div>

                            <div className="equipment-requirements-wrapper mb-20">
                              <div className="label-heading">
                                <p>
                                  {locale("loadingRequirements")}
                                  <span className="star">*</span>
                                </p>
                              </div>
                              <div className="requirements-checkbox">
                                {loadingReqList?.map((value, index) => (
                                  <Field
                                    className="responsive"
                                    component={RadioButton}
                                    name="loadingReq"
                                    id={value.value}
                                    defaultValue={props.values.loadingReq}
                                    onSelect={(e) => {
                                      let tempValue =
                                        props.values.loadingReq ===
                                          e.target?.value
                                          ? ""
                                          : e.target?.value;
                                      props.setFieldValue(
                                        "loadingReq",
                                        tempValue,
                                      );
                                    }}
                                    label={value.label}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>

                          <div>
                            <div className="loan-detail-wrapper">
                              <div className="responsive-mb-32">
                                <div className="label-heading label-heading-lots1">
                                  <p>
                                    {locale("loadDetails")}
                                    <span className="star">*</span>
                                  </p>
                                </div>
                                <NumberInput
                                  className="responsive"
                                  name="quantity"
                                  placeholder={locale("estimatedQuantity")}
                                  maxLength={4}
                                  maxQuantity={9999}
                                  showErrorMessages={true}
                                  decimalScale={0}
                                  onChangeHandler={(value) => { }}
                                />
                              </div>

                              <div className="responsive-mb-32 ">
                                <div className="label-heading label-heading-lots2">
                                  <p>
                                    {locale("preferredPickupDate")}
                                    <span className="star">*</span>
                                  </p>
                                </div>
                                <DatePicker
                                  className="responsive"
                                  name="pickUpDate"
                                  placeholderText=""
                                  selectedValue={props.values?.pickUpDate}
                                  locale={locale}
                                  callBackFun={() => { }}
                                />
                              </div>

                              <div>
                                <div className="label-heading label-heading-lots3">
                                  <p style={{ whiteSpace: "nowrap" }}>
                                    {locale("preferredPickupWindow")}
                                    <span className="star">*</span>
                                  </p>
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "20px",
                                  }}
                                >
                                  <div
                                    className="responsive-mb-32"
                                    style={{ position: "relative" }}
                                  >
                                    <span className="other-label">
                                      {locale("from")}
                                    </span>
                                    <TimePickerInput
                                      className="responsive"
                                      name="appFromTime"
                                      showErrorMessages={true}
                                      fromValue={props.values.appt}
                                      toValue={props.values.apptTo}
                                      selectedTimeValue={
                                        props.values.appFromTime
                                      }
                                      onChangeHandler={(time) => {
                                        setLastChangedTime("fromTime");
                                      }}
                                    />
                                    {lastChangedTime === "fromTime" &&
                                      isTimeErrorCheck(props)}
                                  </div>
                                  <div
                                    className="responsive-mb-32"
                                    style={{ position: "relative" }}
                                  >
                                    <span className="other-label">
                                      {locale("to")}
                                    </span>
                                    <TimePickerInput
                                      className="responsive"
                                      name="appToTime"
                                      endTime="23:00"
                                      fromValue={props.values.appt}
                                      toValue={props.values.apptTo}
                                      showErrorMessages={true}
                                      disabled={
                                        props.values.recurringCheckBoxFlag
                                      }
                                      selectedTimeValue={props.values.appToTime}
                                      onChangeHandler={(time) => {
                                        setLastChangedTime("toTime");
                                      }}
                                    />
                                    {lastChangedTime === "toTime" &&
                                      isTimeErrorCheck(props)}
                                  </div>
                                </div>
                              </div>
                              <div className="responsive-mb-32">
                                <TextInput
                                  className="responsive"
                                  name="poNumber"
                                  placeholder={locale("poNumber")}
                                  inputType={"poNumber"}
                                  maxLength="20"
                                  showErrorMessages={true}
                                />
                              </div>
                            </div>
                          </div>

                          <div>
                            <div className="loan-detail-wrapper comment-section">
                              <div className="responsive-mb-32">
                                <div className="label-heading">
                                  <p>{locale("comments")}</p>
                                </div>
                                <Textarea
                                  className="responsive"
                                  name="comments"
                                  // placeholder="Comments"
                                  inputType={"poNumber"}
                                  maxLength="200"
                                  showErrorMessages={true}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Footer className="button-wrapper">
                        {editedValues ? (
                          <button
                            className={`update-button ${checkDisable(props) && "disabled-button"
                              }`}
                            onClick={() => {
                              formSubmit(props);
                            }}
                            disabled={checkDisable(props)}
                          >
                            {locale("updatePickupRequest")}
                          </button>
                        ) : (
                          <>
                            <button
                              className="reset-button"
                              onClick={() => resetFields(props)}
                            >
                              {locale("reset")}
                            </button>
                            <button
                              className={`submit-button ${checkDisable(props) && "disabled-button"
                                }`}
                              onClick={() => {
                                formSubmit(props);
                              }}
                              disabled={checkDisable(props)}
                            >
                              {locale("createRequest")}
                            </button>
                          </>
                        )}
                      </Footer>
                    </FormWrapper>
                  </Form>
                </>
              );
            }}
          </Formik>

          <AlertModal
            isOpen={modal}
            toggle={toggleModal}
            title={modalTitle}
            content={modalContent}
            link={modalLink}
            className="alert-msg-container"
            InnerClassName="alert-msg"
            callBackAlert={() => {
              if (successCreateOrder) navigate("/manage-orders");
              else toggleModal();
            }}
          />
        </OuterWrapper>
      </Layout>
    );
  };

  return <BodyContainer>{locationListResponse && formFields()}</BodyContainer>;
};

export default RequestPickupOrders;
